import {
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Card,
  CardContent,
  Box,
  Paper
} from "@mui/material";
import { Add, Delete, CloudUpload } from "@mui/icons-material";

const EducationExperienceStep = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched,
  errors
}) => {
  const handleAdd = (key) => {
    setFieldValue(key, [...values[key], {}]);
  };

  const handleRemove = (key, index) => {
    const updated = [...values[key]];
    updated.splice(index, 1);
    setFieldValue(key, updated);
  };

  const handleFieldChange = (key, index, field, value) => {
    const updated = [...values[key]];
    updated[index] = { ...updated[index], [field]: value };
    setFieldValue(key, updated);
  };

  // Upload box with error message support
  const UploadBox = ({ label, onChange, file, error, helperText }) => (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Paper
        variant="outlined"
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          borderStyle: "dashed",
          pl: 2,
          borderRadius: 2,
          cursor: "pointer",
          borderColor: error ? "error.main" : "grey.400",
          "&:hover": { bgcolor: "grey.50" }
        }}
        component="label"
      >
        <CloudUpload fontSize="large" color={error ? "error" : "primary"} />
        <Typography
          sx={{ pl: 1 }}
          variant="body2"
          color={error ? "error" : "text.secondary"}
        >
          {label}
        </Typography>
        <input type="file" hidden onChange={onChange} />
        {file && (
          <Typography variant="caption" display="block" mt={1}>
            📄 {file.name}
          </Typography>
        )}
      </Paper>
      {error && (
        <Typography variant="caption" color="error">
          {helperText}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box width={"100%"} >
      {/* Education Section */}
      <Typography variant="h6" gutterBottom>
        🎓 Education
      </Typography>
      {values.education.map((edu, index) => {
        const eduErrors = errors.education?.[index] || {};
        const eduTouched = touched.education?.[index] || {};

        return (
          <Card
            key={index}
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 3,
              boxShadow: 2,
              position: "relative"
            }}
          >
            <CardContent sx={{ p:2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="College/Institute Name"
                    value={edu.name || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "education",
                        index,
                        "name",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(eduTouched.name && eduErrors.name)}
                    helperText={eduTouched.name && eduErrors.name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Percentage"
                    value={edu.percentage || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "education",
                        index,
                        "percentage",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(
                      eduTouched.percentage && eduErrors.percentage
                    )}
                    helperText={eduTouched.percentage && eduErrors.percentage}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Passing Year"
                    value={edu.passingYear || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "education",
                        index,
                        "passingYear",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(
                      eduTouched.passingYear && eduErrors.passingYear
                    )}
                    helperText={eduTouched.passingYear && eduErrors.passingYear}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UploadBox
                    label="Upload Education Document"
                    onChange={(e) =>
                      handleFieldChange(
                        "education",
                        index,
                        "doc",
                        e.target.files[0]
                      )
                    }
                    file={edu.doc}
                    error={eduTouched.doc && eduErrors.doc}
                    helperText={eduErrors.doc}
                  />
                </Grid>
              </Grid>
              <IconButton
                onClick={() => handleRemove("education", index)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  bgcolor: "error.light",
                  color: "white",
                  "&:hover": { bgcolor: "error.main" }
                }}
                size="small"
              >
                <Delete fontSize="small" />
              </IconButton>
            </CardContent>
          </Card>
        );
      })}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {" "}
        <Button
          startIcon={<Add />}
          onClick={() => handleAdd("education")}
          variant="contained"
          sx={{ mb: 4 }}
        >
          Add Education
        </Button>
      </Box>
      {/* Experience Section */}
      <Typography variant="h6" gutterBottom>
        💼 Experience
      </Typography>
      {values.experience.map((exp, index) => {
        const expErrors = errors.experience?.[index] || {};
        const expTouched = touched.experience?.[index] || {};

        return (
          <Card
            key={index}
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 3,
              boxShadow: 2,
              position: "relative"
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={exp.company || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "company",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(expTouched.company && expErrors.company)}
                    helperText={expTouched.company && expErrors.company}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    label="Joining Date"
                    value={exp.joining || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "joining",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(expTouched.joining && expErrors.joining)}
                    helperText={expTouched.joining && expErrors.joining}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    label="End Date"
                    value={exp.end || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "end",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(expTouched.end && expErrors.end)}
                    helperText={expTouched.end && expErrors.end}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Designation"
                    value={exp.designation || ""}
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "designation",
                        e.target.value
                      )
                    }
                    onBlur={handleBlur}
                    error={Boolean(
                      expTouched.designation && expErrors.designation
                    )}
                    helperText={expTouched.designation && expErrors.designation}
                  />
                </Grid>
                <Grid item xs={12}>
                  <UploadBox
                    label="Upload Experience Document"
                    onChange={(e) =>
                      handleFieldChange(
                        "experience",
                        index,
                        "doc",
                        e.target.files[0]
                      )
                    }
                    file={exp.doc}
                    error={expTouched.doc && expErrors.doc}
                    helperText={expErrors.doc}
                  />
                </Grid>
              </Grid>
              <IconButton
                onClick={() => handleRemove("experience", index)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  bgcolor: "error.light",
                  color: "white",
                  "&:hover": { bgcolor: "error.main" }
                }}
                size="small"
              >
                <Delete fontSize="small" />
              </IconButton>
            </CardContent>
          </Card>
        );
      })}{" "}
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          startIcon={<Add />}
          onClick={() => handleAdd("experience")}
          variant="contained"
        >
          Add Experience
        </Button>
      </Box>
    </Box>
  );
};

export default EducationExperienceStep;
